import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { delay, filter, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { APIService, KPI } from '../api';
import {
  TYPE_OF_USE_BY_PROVIDER_ENERGY_STORAGE_KEY,
  TYPE_OF_USE_BY_PROVIDER_CARBON_STORAGE_KEY,
  EMISSION_FACTOR_PROVIDER_FOR_CARBON_KEY,
  EMISSION_FACTOR_PROVIDER_FOR_ENERGY_KEY
} from './constants';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { SettingsService } from './settings.service';

export const typeOfUse = ['RSM', 'OFF', 'RHS', 'RWB', 'DWH', 'HOT', 'HEC', 'LEI', 'MIX'];
export type TypeOfUse = 'RSM' | 'OFF' | 'RHS' | 'RWB' | 'DWH' | 'HOT' | 'HEC' | 'LEI' | 'MIX';
export const HARD_CODED_TYPE_OF_USE: TypeOfUse = 'OFF';
export type ObjectiveType = 'energy' | 'carbon';

@Injectable({
  providedIn: 'root'
})
export class EmissionFactorService {

  private _countryEmissionFactor$: Observable<KPI.EmissionFactorByCountry[]>;


  private readonly typeOfUseByProviderStorageKeys = {
    energy: TYPE_OF_USE_BY_PROVIDER_ENERGY_STORAGE_KEY,
    carbon: TYPE_OF_USE_BY_PROVIDER_CARBON_STORAGE_KEY,
  };

  private readonly providerStorageKeys = {
    energy: EMISSION_FACTOR_PROVIDER_FOR_ENERGY_KEY,
    carbon: EMISSION_FACTOR_PROVIDER_FOR_CARBON_KEY,
  };

  constructor(
    private api: APIService,
    private translate: TranslateService,
    private http: HttpClient,
  ) { }

  public _providerHasChanged$ = new BehaviorSubject<void>(undefined);
  public providerHasChanged() {
    this._providerHasChanged$.next();
  }

  public getEmissionFactorsByCountries(sitesId: string): Observable<KPI.EmissionFactorByCountry[]> {
    if (!this._countryEmissionFactor$) {
      this._countryEmissionFactor$ = this._providerHasChanged$.pipe(
        switchMap(() => this.api.structures_countries_emission_factors(sitesId)),
        shareReplay(1)
      );
    }
    return this._countryEmissionFactor$;
  }

  public getEmissionFactorsDetailsByCountry(factorId: number): Observable<KPI.EmissionFactorProvider> {
    return this.api.emission_factors_provider_details(factorId);
  }

  public deleteEmissionFactor(providerId: number) {
    return this.api.emission_factors_provider_delete(providerId);
  }

  public getEmissionFactorsList(energyTypeId: number): Observable<KPI.EmissionFactorProvider[]> {
    return this.api.emission_factors_list(energyTypeId);
  }

  public updateProvider(countryId: number, payload: KPI.EmissionProviderPayload): Observable<KPI.EmissionProviderPayload> {
    return this.api.site_emission_factor_provider_patch(countryId, payload).pipe(
      tap(() => this.emissionFactorChanged())
    );
  }

  private _emissionFactorProviders: Observable<KPI.EmissionFactorProvider[]>
  public get emissionFactorProviders$(): Observable<KPI.EmissionFactorProvider[]> {
    if (!this._emissionFactorProviders) {
      this._emissionFactorProviders = this.api.emission_factors_providers().pipe(
        map((providers) => [
          ...providers.filter((provider) => provider.show_as_objective),
          {
            id: 0,
            name: this.translate.instant('No objective'),
            description: this.translate.instant('No objective'),
            is_default: false,
            show_as_objective: false,
            based: null,
          },
          {
            id: -1,
            name: this.translate.instant('Site by site objectives'),
            description: this.translate.instant('Site by site objectives'),
            is_default: false,
            show_as_objective: true,
            based: null,
          }
        ]),
        shareReplay(1),
      );
    }
    return this._emissionFactorProviders;
  }

  /**
   * Observables used to notify the type of use by provider map has been updated
   */
  public typeOfUseByProviderUpdated = {
    energy: new BehaviorSubject<boolean>(false),
    carbon: new BehaviorSubject<boolean>(false),
  };

  /**
   * Type of use by provider map
   */
  private typeOfUseByProvider: { [key in ObjectiveType]: { [provider: number]: TypeOfUse } } = {
    energy: {},
    carbon: {},
  };
  public typeOfUseByProvider$(type: ObjectiveType): Observable<{ [provider: number]: TypeOfUse }> {
    this.setUpDefaultProviderMap(type);

    return this.typeOfUseByProviderUpdated[type].pipe(
      filter((updated) => !!updated),
      map(() => this.typeOfUseByProvider[type]),
    );
  }

  /**
   * Update the type of use by provider map
   */
  public setTypeOfUseByProvider(type: ObjectiveType, providerId: number, typeOfUse: TypeOfUse): void {
    this.setUpDefaultProviderMap(type);

    if (!this.typeOfUseByProvider[type]) {
      this.typeOfUseByProvider[type] = {};
    }

    this.typeOfUseByProvider[type][providerId] = typeOfUse;
    this.typeOfUseByProviderUpdated[type].next(true);
    this.saveTypeOfUseByProviderInCache(type);
  }

  /**
   * Save the type of use by provider map in the local storage
   */
  private saveTypeOfUseByProviderInCache(type: ObjectiveType): void {
    const storageKey = this.typeOfUseByProviderStorageKeys[type];
    localStorage.setItem(storageKey, JSON.stringify(this.typeOfUseByProvider[type]));
  }

  /**
   * Get the selected type of use for Energy or Carbon
   */
  public getSelectedTypeOfUse$(type: ObjectiveType): Observable<TypeOfUse> {
    return combineLatest([
      this.typeOfUseByProvider$(type),
      this.getSelectedProvider$(type),
    ]).pipe(
      map(([typeOfUseByProvider, provider]) => {
        // Force OFF for No objective mode
        if (provider?.id === 0) {
          return HARD_CODED_TYPE_OF_USE;
        }
        return typeOfUseByProvider[provider?.id];
      }),
    );
  }

  private settings = inject(SettingsService);
  /**
   * Get the selected provider for Energy or Carbon
   */
  private selectedProvider: { [key: string]: BehaviorSubject<number> } = {
    energy: new BehaviorSubject<number>(undefined),
    carbon: new BehaviorSubject<number>(undefined),
  };
  public getSelectedProvider$(type: ObjectiveType): Observable<KPI.EmissionFactorProvider> {
    this.setUpDefaultSelectedProvider(type);

    return combineLatest([
      this.emissionFactorProviders$,
      this.selectedProvider[type],
    ]).pipe(
      map(([emissionFactorProviders, selectedProviderId]) => {
        let provider: KPI.EmissionFactorProvider;

        if (selectedProviderId !== undefined && selectedProviderId !== null) {
          provider = emissionFactorProviders.find(({ id }) => id === selectedProviderId);
        } else {
          provider = this.getDefaultProviderFromSettings(type, emissionFactorProviders);
        }

        if (!provider && emissionFactorProviders?.length) {
          provider = emissionFactorProviders[0];
        } else if (!emissionFactorProviders?.length) {
          console.error(`No provider found in database`);
        }

        return provider;
      }),
    );
  }

  private getDefaultProviderFromSettings(type: ObjectiveType, providersList: KPI.EmissionFactorProvider[])
  : KPI.EmissionFactorProvider {
    let provider: KPI.EmissionFactorProvider;
    const key = type === 'energy' ? 'ENERGY__DEFAULT_ENERGY_OBJECTIVE' : 'ENERGY__DEFAULT_CARBON_OBJECTIVE';
    const defaultProviderSetting = this.settings.getSettingValue(key, null);
    if (defaultProviderSetting === 1) {
      provider = providersList.find(p => p.based === "LOCATION");
    } else if (defaultProviderSetting === 2) {
      provider = providersList.find(p => p.id === -1);
    }
    return provider;
  }

  private defaultSelectedProvidersMapLoaded: { [key: string]: boolean } = { energy: false, carbon: false };
  private setUpDefaultSelectedProvider(type: ObjectiveType): void {
    if (!this.defaultSelectedProvidersMapLoaded[type]) {
      const storageKey = this.providerStorageKeys[type];
      let selectedProviderId = JSON.parse(localStorage.getItem(storageKey));
      this.selectedProvider[type].next(selectedProviderId);
      this.defaultSelectedProvidersMapLoaded[type] = true;
    }
  }

  public setSelectedProvider(type: ObjectiveType, providerId: number): void {
    this.selectedProvider[type].next(providerId);
    this.saveSelectedProviderInCache(type, providerId);
  }

  /**
   * Save the type of use by provider map in the local storage
   */
  private saveSelectedProviderInCache(type: ObjectiveType, providerId: number): void {
    const storageKey = this.providerStorageKeys[type];
    localStorage.setItem(storageKey, JSON.stringify(providerId));
  }

  private defaultProvidersMapLoaded: { [key: string]: boolean } = { energy: false, carbon: false };
  private setUpDefaultProviderMap(type: ObjectiveType): void {
    if (!this.defaultProvidersMapLoaded[type]) {
      this.emissionFactorProviders$.pipe(
        first(),
      ).subscribe((emissionFactorProviders) => {
        const storageKey = this.typeOfUseByProviderStorageKeys[type];
        let typeOfUseByProvider = JSON.parse(localStorage.getItem(storageKey));
        if (!typeOfUseByProvider) {
          typeOfUseByProvider = {};
          emissionFactorProviders.map((provider) => typeOfUseByProvider[provider.id] = HARD_CODED_TYPE_OF_USE);
        }
        this.typeOfUseByProvider[type] = { ...typeOfUseByProvider, ...this.typeOfUseByProvider[type] };
        this.saveTypeOfUseByProviderInCache(type);
        this.typeOfUseByProviderUpdated[type].next(true);
        this.defaultProvidersMapLoaded[type] = true;
      });
    }
  }

  private _emissionFactorChanged$ = new BehaviorSubject<void>(undefined);
  public emissionFactorChanged() {
    this._emissionFactorChanged$.next();
  }

  private _emissionFactorProvidersBySite: {[energyTypeId: number]: Observable<KPI.EmissionFactorBySiteResponse>} = {};
  public emissionFactorProvidersBySite(energyTypeId: number): Observable<KPI.EmissionFactorBySiteResponse> {
    if (!this._emissionFactorProvidersBySite[energyTypeId]) {
      this._emissionFactorProvidersBySite[energyTypeId] = this._emissionFactorChanged$.pipe(
        switchMap(() => this.api.emission_factors_providers_by_site(energyTypeId)),
        shareReplay(1)
      )
    }
    return this._emissionFactorProvidersBySite[energyTypeId];
  }

  public createProviderFromExcelFile(file: File): Observable<any> {

    const formData = new FormData();
    formData.append("import_file", file);

    const endpoint = '/emission-factors/api/providers/';

    return this.http.post(`${environment.backendHost}${endpoint}`, formData);
  }

  public updateProviderEmissionFactor(
    providerId: number,
    energyTypeId: number,
    year: number,
    factor: number,
  ): Observable<any> {
    return this.api.patch_provider_emission(
      providerId,
      {
        energy_type: energyTypeId,
        emission_factors: [{
          year,
          factor,
        }],
      }
    )
  }

  public downloadProvider(providerId: number, energyTypeId: number): Observable<any> {
    const endpoint = `/emission-factors/api/providers/${providerId}/energy-type/${energyTypeId}`
    const headers = new HttpHeaders();

    return this.http.request(
      'GET',
      `${environment.backendHost}${endpoint}`,
      {
        body: {},
        params: {},
        responseType: 'blob',
        observe: 'response',
        headers,
      },
    ).pipe(
      map((response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        const fileName = contentDisposition ? contentDisposition.split('filename=')[1].replace(/"/g, '') : "file";

        const blob = new Blob([response.body], { type: response.headers.get('content-type') });

        const downloadURL = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadURL;
        a.download = fileName;
        a.click();

        return downloadURL;
      }),
      delay(100),
      map((downloadURL) => window.URL.revokeObjectURL(downloadURL)),
    );
  }


}
